import { DocumentAIAnalysis, Document } from "../types";
import { db, auth } from "./firebase";

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const getAuthToken = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("User not authenticated");
    return await currentUser.getIdToken();
};

// Helper to call Backend AI Proxy
const generateContent = async (prompt: string, base64Image?: { data: string, mimeType: string }) => {
    const token = await getAuthToken();

    const body: any = { prompt };
    if (base64Image) {
        // Backend expects 'image' as base64 string (potentially with prefix or not, let's verify backend logic)
        // Backend: image.replace(/^data:image\/\w+;base64,/, "")
        // So we can send it as is or clean. 
        // Let's send a data URI if possible, or just the base64 data.
        // My backend code: const imageParts = [{ inlineData: { data: image.replace(...) } }]
        // So I should send the full data URI or just the base64? 
        // The backend `image` param is used.
        // Let's construct a data URI.
        body.image = `data:${base64Image.mimeType};base64,${base64Image.data}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/ask`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const err = await response.text();
        console.error("AI Proxy Error:", err);
        throw new Error(`AI Service Error: ${err}`);
    }

    const data = await response.json();
    return data.answer;
};

export const analyzeDocument = async (
    file: File,
    // apiKey: string,  <-- REMOVED
    existingDocId: string,
    companyId: string
): Promise<DocumentAIAnalysis> => {

    // Convert file to base64
    const base64Data = await fileToGenerativePart(file);

    const prompt = `
    Analyze this document completely and strictly.
    Extract the following structured data in JSON format only.
    Do NOT interpret or guess values that are not visible.
    If a value is missing, use null.

    Required JSON Schema:
    {
        "doc_type": "invoice" | "receipt" | "bank_statement" | "contract" | "other",
        "doc_date": "YYYY-MM-DD" or null,
        "details": {
            "issuer": "string" or null,
            "recipient": "string" or null,
            "vat_number": "string" or null,
            "invoice_number": "string" or null
        },
        "amounts": {
            "net": number (float),
            "vat": number (float),
            "gross": number (float),
            "currency": "EUR" | "USD" | "GBP"
        },
        "summary": "1-2 sentence semantic summary of what this document is about.",
        "keywords": ["tag1", "tag2", "tag3"],
        "confidence_score": number (0.0 - 1.0 based on visibility),
        "installment_info": {
            "is_installment": boolean,
            "installment_date": "YYYY-MM-DD" or null (Date of the specific installment, e.g. from 'Ποσό δόσης της 30/01/2026'),
            "installment_amount": number (float) or null,
            "installment_number": number or null,
            "total_debt_amount": number (float) or null,
            "payment_code": string or null
        }
    }

    Special Instruction for Greek Documents:
    - Look for the phrase "Ποσό δόσης" (or "Ποσό Δόσης", case-insensitive) or "Ποσό δόσης της" followed by a date.
    - If found, set "is_installment" to true and extract the date and amount.
    - Look for "Συνολικό Ποσό Οφειλής", "Συνολικό ποσό οφειλής δήλωσης", or "ΠΛΗΡΩΤΕΟ ΠΟΣΟ" to extract "total_debt_amount".
    - Look for "Ταυτότητα Οφειλής", "Κωδικός Πληρωμής", or "BARCODE" (especially if it starts with "RF") to extract "payment_code" (remove spaces).
    - "doc_date" should be the issue date.
    - "installment_date" should be the specific due date, often labeled as "ΗΜ/ΝΙΑ ΕΜΠΡΟΘΕΣΜΗΣ ΠΛΗΡΩΜΗΣ" or "Προθεσμία πληρωμής".
    - **SPECIAL RULE FOR e-EFKA**: For "e-ΕΦΚΑ" documents, the due date is often located **BELOW** the label "ΗΜ/ΝΙΑ ΕΜΠΡΟΘΕΣΜΗΣ ΠΛΗΡΩΜΗΣ". Look at the line immediately following this header.
    `;

    try {
        const text = await generateContent(prompt, base64Data.inlineData);

        // Clean markdown code blocks if present
        const jsonString = text.replace(/```json/g, '').replace(/```/g, '').trim();
        const data = JSON.parse(jsonString);

        // Enhance with metadata
        const analysis: DocumentAIAnalysis = {
            doc_id: existingDocId,
            company_id: companyId,
            doc_type: data.doc_type || 'unknown',
            doc_date: data.doc_date,
            confidence_score: data.confidence_score || 0.5,
            details: data.details,
            amounts: data.amounts,
            summary: data.summary,
            keywords: data.keywords || [],
            installment_info: data.installment_info,
            period: data.doc_date ? {
                month: new Date(data.doc_date).getMonth() + 1,
                year: new Date(data.doc_date).getFullYear()
            } : undefined,
            processed_at: new Date().toISOString(),
            model_used: "gemini-1.5-flash"
        };

        return analysis;

    } catch (error) {
        console.error("AI Analysis Failed:", error);
        throw new Error("Failed to analyze document with AI.");
    }
};

// Interface for the Chat Response
export interface AiChatResponse {
    answer: string;
    sources: Document[];
}

export const askDocumentAssistant = async (
    question: string,
    companyId: string
    // apiKey: string <-- REMOVED
): Promise<AiChatResponse> => {

    // Step 1: Intent & Filter Extraction
    const filterPrompt = `
    You are a query generator for a Firestore database of accounting documents.
    Current Date: ${new Date().toISOString().split('T')[0]}
    
    User Question: "${question}"

    The 'documents' collection has fields: 
    - clientId (string)
    - ai_analysis.doc_type (string: invoice, receipt, bank_statement, etc.)
    - ai_analysis.doc_date (string: YYYY-MM-DD)
    - ai_analysis.keywords (array of strings)

    Rules for 'latest' or 'current' period:
    - If user asks for "latest", "last month", "current period", or "recent", set "startDate" to the first day of the CURRENT month (or last month if near start of month).
    - If user asks for specific year (e.g. 2026), set startDate/endDate accordingly.
    - Do NOT default to null if a time period is implied.

    Return a JSON object ONLY with the following structure to answer the question optimally:
    {
        "intent": "retrieval",
        "filters": {
            "startDate": "YYYY-MM-DD" or null,
            "endDate": "YYYY-MM-DD" or null,
            "docTypes": ["invoice", "receipt"] or null (ONLY if user EXPLICITLY names a type like 'invoices'. Default to null),
            "limit": number (default 20, max 50)
        }
    }
    `;

    let filters: any = {};
    try {
        const responseText = await generateContent(filterPrompt);
        const cleanJson = responseText.replace(/```json/g, '').replace(/```/g, '').trim();
        filters = JSON.parse(cleanJson);
    } catch (e) {
        console.error("Filter extraction failed", e);
        filters = { intent: "retrieval", filters: { limit: 20 } };
    }

    // Step 2: Firestore Query
    console.log("[Chatbot] Filters extracted:", filters);

    let query = db.collection("documents").where("clientId", "==", companyId);

    try {
        if (filters.filters?.startDate) {
            query = query.where("ai_analysis.doc_date", ">=", filters.filters.startDate);
        }
        if (filters.filters?.endDate) {
            query = query.where("ai_analysis.doc_date", "<=", filters.filters.endDate);
        }
        if (filters.filters?.docTypes && filters.filters.docTypes.length > 0) {
            query = query.where("ai_analysis.doc_type", "in", filters.filters.docTypes);
        }

        query = query.orderBy("ai_analysis.doc_date", "desc");
        const limit = filters.filters?.limit || 20;
        query = query.limit(limit);

        const snapshot = await query.get();
        const documents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

        console.log(`[Chatbot] Found ${documents.length} documents.`);

        if (documents.length === 0) {
            return {
                answer: "I couldn't find any relevant documents in your account matching that criteria.",
                sources: []
            };
        }

        // Step 3: Context Assembly
        const context = documents.map((doc: any) => {
            const analysis = doc.ai_analysis || {};
            const effectiveInstallment = analysis.installment_info?.installment_amount || analysis.installment_info?.total_debt_amount || 'N/A';
            const installmentText = analysis.installment_info?.is_installment
                ? `\nInstallment: YES. Due: ${analysis.installment_info.installment_date || 'N/A'}. Amount: ${effectiveInstallment}. Debt: ${analysis.installment_info.total_debt_amount || 'N/A'}`
                : '';

            return `[Doc ID: ${doc.id}]
Type: ${analysis.doc_type || doc.typeName}
Date: ${analysis.doc_date || doc.createdAt}
Amounts: Net ${analysis.amounts?.net}, VAT ${analysis.amounts?.vat}, Gross ${analysis.amounts?.gross} ${analysis.amounts?.currency}${installmentText}
Entities: ${analysis.details?.issuer} -> ${analysis.details?.recipient}
Summary: ${analysis.summary}
`;
        }).join("\n---\n");

        // Step 4: Final Answer Generation
        const answerPrompt = `
    You are a strict accounting assistant. 
    Answer the user's question using ONLY the provided document context.
    
    User Question: "${question}"
    
    Context:
    ${context}
    
    Rules:
    1. DETECT the language of the User Question and ANSWER IN THAT SAME LANGUAGE. (e.g. if Greek, answer in Greek).
    2. If the answer is not in the context, state that you cannot find the information (in the user's language).
    3. Start your answer directly. Be professional and concise.
    4. MANDATORY: When using information from a document, YOU MUST Cite the Document ID in brackets like [doc_id] at the end of the sentence.
    5. Provide totals if asked (sum up the values from context).
    6. TIME SENSITIVITY: If the user asks for "latest", "last period", or "current", IGNORE older documents in the context. Focus ONLY on the most recent ones (based on Date or Due Date).
    7. DEBT LOGIC: When discussing obligations, refer to the "Installment Due Date" if available.
    `;

        const answerText = await generateContent(answerPrompt);

        const citedDocIds = new Set<string>();
        const citationRegex = /\[([^\]]+)\]/g;
        let match;

        while ((match = citationRegex.exec(answerText)) !== null) {
            citedDocIds.add(match[1]);
        }

        const relevantSources = documents.filter((doc: any) => citedDocIds.has(doc.id));
        const cleanAnswer = answerText.replace(/\[([^\]]+)\]/g, '').replace(/\s{2,}/g, ' ').trim();

        return {
            answer: cleanAnswer,
            sources: relevantSources as Document[]
        };

    } catch (queryError: any) {
        console.error("[Chatbot] Firestore Query Failed:", queryError);
        return {
            answer: "I am having trouble accessing your documents. Please try a simpler question.",
            sources: []
        };
    }
};

async function fileToGenerativePart(file: File): Promise<{ inlineData: { data: string; mimeType: string } }> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve({
                inlineData: {
                    data: base64String,
                    mimeType: file.type
                }
            });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}
